import {useQuery, useQueryClient} from "@tanstack/react-query";
import {getCompanyBranding, getCompanyEffectiveBranding} from "../services/companyBrandingService";
import {CompanyBrandingResponse} from "../services/companyBrandingService";
import {
    getBrandingCache,
    getBrandingCacheTimestamp,
    setBrandingCache,
    removeBrandingCache,
} from "../utils/brandingCacheUtils";
import {useEffect} from "react";
import {useCompanyFeatureFlag, useFindFirstParentCompanyFeatureFlag} from "./featureFlagsHook";
import {FeatureFlagEnum} from "../types/featureFlagsEnum";

const BRANDING_CACHE_KEY = "company-branding";
const STALE_TIME = 1000 * 60 * 30; // 30 minutes
const GC_TIME = 1000 * 60 * 60 * 24 * 30; // 30 days

export const useAdminCompanyBrandingQuery = (companyId?: string) => {
    return useQuery<CompanyBrandingResponse>({
        queryKey: ["adminBranding", companyId],
        queryFn: async () => {
            if (!companyId) throw new Error("No company ID");
            return await getCompanyBranding(companyId);
        },
        enabled: !!companyId,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
};

export const useCompanyBrandingQuery = (companyId?: string) => {
    const cachedData = getBrandingCache(companyId);
    const cachedTimestamp = getBrandingCacheTimestamp(companyId);
    const whiteLabelingEnabledOnCompany = useCompanyFeatureFlag(FeatureFlagEnum.whiteLabeling);
    const whiteLabelingParentId = useFindFirstParentCompanyFeatureFlag(FeatureFlagEnum.whiteLabeling);
    const enabled = whiteLabelingEnabledOnCompany || !!whiteLabelingParentId;
    const targetCompanyId = whiteLabelingEnabledOnCompany ? companyId : whiteLabelingParentId;

    // Invalidate the cache if the cached data is not present or if the companyId changes
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!cachedData && targetCompanyId) {
            void queryClient.invalidateQueries({queryKey: [BRANDING_CACHE_KEY, targetCompanyId]});
        }
    }, [targetCompanyId, cachedData, queryClient]);

    return useQuery<string | null>({
        queryKey: [BRANDING_CACHE_KEY, targetCompanyId],
        queryFn: async () => {
            if (!targetCompanyId) throw new Error("No company ID");
            const branding = await getCompanyEffectiveBranding(targetCompanyId);
            if (branding?.isEnabled && branding?.logoUrl) {
                setBrandingCache(targetCompanyId, branding.logoUrl, true);
                return branding.logoUrl;
            } else {
                removeBrandingCache(targetCompanyId);
                return null;
            }
        },
        initialData: cachedData,
        initialDataUpdatedAt: cachedTimestamp,
        enabled,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};
